// src/components/DashboardParts.jsx
// ダッシュボードの共通部品【2026-09-18】
//
//   「標準」の並べ方は、画面を重要度で4つの層に分ける。
//     0層 いま動いているもの … 受付中のセッション。AttendanceStartCard がそのまま担当する
//     1層 要対応            … 低出席率・名簿なし・閉じ忘れ。件数つきで1枚にまとめる
//     2層 数字              … 統計タイル。色を外してグレーに落とす
//     3層 一覧              … 担当授業・履歴。折りたたんで件数だけ見せる
//
//   考え方は「色は重要度にだけ使う」。統計4枚を青・緑・橙・紫で塗ると、
//   本当に赤いもの（60%未満の学生など）が同じ強さになって埋もれる。
//
//   ここには1〜3層の部品と、教員・学生で共通の小物を置く。
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from './Icon';

// ── よく使う画面へ1タップで飛ぶボタン ──────────────────────────
//   サイドバーと重複するが、サイドバーはスマホだと☰の中に隠れる。
//   ダッシュボードは最初に開く画面なので、行き先をそのまま並べておく。
//   items: [{ to, icon, label }]
export function QuickLinks({ items = [], title = 'よく使う画面' }) {
  if (items.length === 0) return null;
  return (
    <nav className="dash-qa" aria-label={title}>
      {items.map(it => (
        <Link key={it.to} to={it.to} className="dash-qa-btn">
          <span className="dash-qa-icon"><Icon name={it.icon} size={20} /></span>
          <span className="dash-qa-label">{it.label}</span>
        </Link>
      ))}
    </nav>
  );
}

// ── 別ページへ送り出す1行の帯 ──────────────────────────────────
//   中身が重い画面（出席受付など）は、ダッシュボードに埋め込まずに
//   状況だけ見せてリンクで送る。埋め込むと同じ画面が2か所にできて、
//   どちらを触っているのか分からなくなる。
export function LinkStrip({ to, icon, title, meta, action = 'こちら', live }) {
  return (
    <Link to={to} className={'dash-strip' + (live ? ' is-live' : '')}>
      <span className="dash-strip-icon">
        {live ? <i className="dash-pulse" aria-hidden="true" /> : <Icon name={icon} size={20} />}
      </span>
      <span className="dash-strip-body">
        <span className="dash-strip-t">{title}</span>
        {meta && <span className="dash-strip-m">{meta}</span>}
      </span>
      <span className="dash-strip-go">{action} →</span>
    </Link>
  );
}

// ── 1層: 要対応 ────────────────────────────────────────────────
/**
 * items: [{ key, level: 'crit'|'warn'|'note', title, meta, to, action, detail, detailLabel, dismissId }]
 *   to が無い項目はリンクにしない（同じ画面の下にあるカードで対応するものなど）。
 *   【2026-10-07】dismissId がある項目は「確認済みにする」で隠せる（このブラウザに覚える）。
 *     dismissId は中身の印（例：出席率の低い学生の顔ぶれ）。中身が変わる（新しい学生が増える）と、また出る。
 */
const DISMISS_KEY = 'dashAlertDismissed';
const readDismissed = () => { try { return JSON.parse(localStorage.getItem(DISMISS_KEY) || '{}') || {}; } catch (e) { return {}; } };
export function AlertCard({ title = '要対応', items: allItems = [], emptyText }) {
  const [dismissed, setDismissed] = useState(readDismissed);
  const [showHidden, setShowHidden] = useState(false);
  const save = (next) => { setDismissed(next); try { localStorage.setItem(DISMISS_KEY, JSON.stringify(next)); } catch (e) { /* 覚えられなくても動く */ } };
  const isHidden = (it) => it.dismissId && dismissed[it.key] === it.dismissId;
  const hidden = allItems.filter(isHidden);
  const items = showHidden ? allItems : allItems.filter(it => !isHidden(it));
  const dismiss = (it) => save({ ...dismissed, [it.key]: it.dismissId });
  const undismiss = (it) => { const n = { ...dismissed }; delete n[it.key]; save(n); };
  const hiddenNote = hidden.length > 0 && (
    <button type="button" className="dash-hidden-toggle" onClick={() => setShowHidden(v => !v)}
            style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.8rem', cursor: 'pointer', padding: '0.2rem 0' }}>
      {showHidden ? '確認済みの項目を隠す' : `確認済みで隠している項目 ${hidden.length}件を表示`}
    </button>
  );
  if (items.length === 0) {
    if (!emptyText && !hiddenNote) return null;
    if (!emptyText) return null;
    return (
      <div className="card dash-alert-card is-clear">
        <div className="card-header"><h2>{title}</h2></div>
        <p className="dash-alert-clear">{emptyText}</p>
        {hiddenNote}
      </div>
    );
  }
  const worst = items.some(i => i.level === 'crit') ? 'crit'
              : items.some(i => i.level === 'warn') ? 'warn'
              : 'note';
  return (
    <div className={'card dash-alert-card ' + worst}>
      <div className="card-header">
        <h2>{title} <span className={'dash-count ' + worst}>{items.length}</span></h2>
      </div>
      {items.map((it, i) => (
        <AlertRow key={it.key || i} {...it} hidden={isHidden(it)}
                  onDismiss={it.dismissId ? () => (isHidden(it) ? undismiss(it) : dismiss(it)) : null} />
      ))}
      {hiddenNote}
    </div>
  );
}

function AlertRow({ level = 'note', title, meta, to, action, detail, detailLabel, onDismiss, hidden }) {
  const body = (
    <>
      <i className={'dash-sev ' + level} aria-hidden="true" />
      <span className="dash-alert-body">
        <span className="dash-alert-t">{title}</span>
        {meta && <span className="dash-alert-m">{meta}</span>}
      </span>
      {action && <span className="dash-alert-go">{action} →</span>}
    </>
  );
  return (
    <div className="dash-alert-wrap" style={hidden ? { opacity: 0.55 } : undefined}>
      {to
        ? <Link to={to} className="dash-alert">{body}</Link>
        : <div className="dash-alert">{body}</div>}
      {onDismiss && (
        <button type="button" onClick={onDismiss}
                title={hidden ? 'また出すようにします' : 'いまの内容を確認済みにして隠します。新しい学生が増えたら、また出ます'}
                style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: '0.78rem', cursor: 'pointer', padding: '0.1rem 0' }}>
          {hidden ? '↺ また出す' : '✓ 確認済みにする（隠す）'}
        </button>
      )}
      {detail && (
        <details className="dash-detail">
          <summary>{detailLabel || '内訳を見る'}</summary>
          <div className="dash-detail-body">{detail}</div>
        </details>
      )}
    </div>
  );
}

// ── 2層: 数字（抑えめ） ────────────────────────────────────────
export function StatTiles({ children }) {
  return <div className="dash-stats">{children}</div>;
}

/** hero を付けた1枚だけ大きく見せる（ふつうは平均出席率） */
export function StatTile({ label, value, unit, hero, note, color, children }) {
  return (
    <div className={'dash-stat' + (hero ? ' hero' : '')}>
      <div className="dash-stat-l">{label}</div>
      <div className="dash-stat-v" style={color ? { color } : undefined}>
        {value}{unit && <small>{unit}</small>}
      </div>
      {children}
      {note && <div className="dash-stat-note">{note}</div>}
    </div>
  );
}

/**
 * 小さな折れ線。values は古い順の数値。
 * 幅は 220 固定で、CSS の max-width:100% で縮む（伸ばすと線が歪むため）。
 */
export function Sparkline({ values, color = '#2563eb', label }) {
  const nums = (values || []).map(Number).filter(n => !isNaN(n));
  if (nums.length < 2) return null;
  const W = 220, H = 34, P = 6;
  const min = Math.min(...nums);
  const max = Math.max(...nums);
  const span = max - min || 1;
  const x = (i) => P + (i * (W - P * 2)) / (nums.length - 1);
  const y = (v) => P + (1 - (v - min) / span) * (H - P * 2);
  const pts = nums.map((v, i) => x(i).toFixed(1) + ',' + y(v).toFixed(1)).join(' ');
  const last = nums.length - 1;
  return (
    <svg className="dash-spark" width={W} height={H} viewBox={`0 0 ${W} ${H}`}
         role="img" aria-label={label || ('推移 ' + nums.join('、'))}>
      <polygon points={`${pts} ${x(last).toFixed(1)},${H} ${x(0).toFixed(1)},${H}`}
               fill={color} opacity="0.08" />
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={x(last)} cy={y(nums[last])} r="3.5" fill={color} />
    </svg>
  );
}

/** 出席率のドーナツ（学生の全体表示） */
export function Donut({ rate, color, size = 112 }) {
  const r = 45;
  const c = 2 * Math.PI * r;
  const on = Math.max(0, Math.min(100, Number(rate) || 0)) / 100 * c;
  return (
    <svg className="dash-donut" width={size} height={size} viewBox="0 0 112 112"
         role="img" aria-label={'総合出席率 ' + Math.round(rate) + '%'}>
      <circle cx="56" cy="56" r={r} fill="none" stroke="#eef1f6" strokeWidth="13" />
      <circle cx="56" cy="56" r={r} fill="none" stroke={color} strokeWidth="13" strokeLinecap="round"
              strokeDasharray={`${on.toFixed(1)} ${c.toFixed(1)}`} transform="rotate(-90 56 56)" />
      <text x="56" y="53" textAnchor="middle" fontSize="25" fontWeight="700" fill="#334155">
        {Math.round(rate)}
      </text>
      <text x="56" y="70" textAnchor="middle" fontSize="11" fill="#8a93a3">％</text>
    </svg>
  );
}

// ── 3層: 折りたたみ ────────────────────────────────────────────
export function Fold({ title, count, open, children }) {
  return (
    <details className="dash-fold" open={open}>
      <summary>
        {title}
        {count != null && <span className="dash-fold-c">{count}</span>}
      </summary>
      <div className="dash-fold-body">{children}</div>
    </details>
  );
}

// ── 教員・学生で共通の小物 ─────────────────────────────────────
export function StatusBadge({ status }) {
  const map = {
    present: { text: '出席', bg: '#dcfce7', fg: '#166534' },
    late:    { text: '遅刻', bg: '#fef3c7', fg: '#92400e' },
    absent:  { text: '欠席', bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = map[status] || { text: status, bg: '#e5e7eb', fg: '#374151' };
  return (
    <span style={{
      display: 'inline-block', padding: '0.2rem 0.7rem', borderRadius: '12px',
      background: s.bg, color: s.fg, fontSize: '0.8rem', fontWeight: 600,
    }}>{s.text}</span>
  );
}

export function EmptyState({ icon, text }) {
  return (
    <div style={{ textAlign: 'center', padding: '2rem 1rem', color: '#888' }}>
      {icon ? <div style={{ marginBottom: '0.5rem', color: '#c7ccd4' }}>{icon}</div> : null}
      <p>{text}</p>
    </div>
  );
}

export function formatTime(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleTimeString('ja-JP', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch (e) { return '-'; }
}

/** 表を横スクロールで逃がす枠 */
export function TableWrap({ children }) {
  return <div className="table-wrap">{children}</div>;
}
